import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useStore } from '../../../stores/root';
import styles from './styles.module.scss';
import { 
    IOperationItem, 
    TOperationActivityType, 
    TOperationStatusFilter, 
    TOperationTypeFilter, 
    TOperationViewMode 
} from './types';
import WorkspaceHeader from './header';
import WorkspaceTableView from './views/table';
import WorkspaceBoardView from './views/board';
import RescheduleModal from './modals/reschedule';
import QuickStatusModal from './modals/quickStatus';
import ActivitiesService from '../../../services/activities/activities';
import { IActivityResourceShortProps } from '../../../props/activities';
import LoadingComponent from '../../feedbacks/loading';
import ErrorService from '../../../services/general/error';
import { faAnchor, faBox, faBoxesPacking, faCheck, faDolly, faShip, faTag, faTruck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import OrderService from '../../../services/orders/order';

const OperationsWorkspace: React.FC = () => {
    const { banner } = useStore();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [activities, setActivities] = useState<IActivityResourceShortProps | undefined>();

    // Filter & Navigation States
    const [selectedDate, setSelectedDate] = useState<{ startDate: string; endDate: string }>({
        startDate: moment().startOf('day').toISOString(),
        endDate: moment().endOf('day').toISOString(),
    });
    const [statusFilter, setStatusFilter] = useState<TOperationStatusFilter>('all');
    const [typeFilter, setTypeFilter] = useState<TOperationTypeFilter>('all');
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [viewMode, setViewMode] = useState<TOperationViewMode>('table');

    // Modal States
    const [activeRescheduleItem, setActiveRescheduleItem] = useState<IOperationItem | undefined>();
    const [activeStatusItem, setActiveStatusItem] = useState<IOperationItem | undefined>();

    useEffect(() => {
        loadActivities();
    }, [selectedDate]);

    const loadActivities = async () => {
        try {
            setLoaded(false);
            const qs = `startDate=${selectedDate.startDate}&endDate=${selectedDate.endDate}`;
            const res = await ActivitiesService.retrieve(qs);
            setActivities(res);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'load_operations_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Gagal memuat data operasional. Error: ' + ErrorService.getMessage(e),
            });
            setLoaded(true);
        }
    };

    // Normalize raw backend activities into unified IOperationItem[]
    const allItems: IOperationItem[] = useMemo(() => {
        if (!activities) return [];
        const items: IOperationItem[] = [];
        const today = moment().startOf('day');

        // 1. Loading Trackings
        (activities.loading || []).forEach((loading) => {
            const details = loading.loadDetails;
            const dateStr = details?.actualStartDate || details?.estimatedStartDate || loading.createdAt || moment().toISOString();
            const itemDate = moment(dateStr);
            const rawStatus = (loading.status || 'scheduled').toLowerCase();
            const isDone = rawStatus === 'completed' || rawStatus === 'done';
            const isInProg = rawStatus === 'in_progress' || rawStatus === 'running';
            const isOverdue = !isDone && itemDate.isBefore(today, 'day');
            const overdueDays = isOverdue ? today.diff(itemDate, 'days') : 0;

            const orderNum = loading.order?.orderNumber || loading.orderNumber;
            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (loading.orderId ? `Order #${loading.orderId.substring(0, 8)}` : '');

            items.push({
                id: `load_${loading.id}`,
                rawId: loading.id,
                itemType: 'tracking',
                activityType: 'loading',
                activityLabel: 'Muat Depo (Loading)',
                activityColor: '#f59e0b',
                activityIcon: faBoxesPacking,
                orderId: loading.orderId,
                orderNumber: orderLabel,
                customerName: (loading.order as any)?.customer?.name,
                containers: loading.container ? [{
                    id: loading.container.id,
                    containerNumber: loading.container.containerNumber || 'No Container',
                    sealNumber: loading.container.sealNumber,
                    size: loading.container.size
                }] : [],
                locationText: details?.address && details.address !== 'ededed' ? details.address : 'Depo / Gudang Muat',
                scheduledDate: dateStr,
                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),
                actualDate: details?.actualStartDate,
                isOverdue,
                overdueDays,
                status: rawStatus,
                normalizedStatus: isDone ? 'completed' : (isOverdue ? 'overdue' : (isInProg ? 'in_progress' : 'scheduled')),
                statusLabel: isDone ? 'Selesai' : (isInProg ? 'Sedang Muat' : 'Terjadwal'),
                detailsLink: `/orders/${loading.orderId}/trackings`,
                orderLink: `/orders/${loading.orderId}`,
                rawObject: loading,
            });
        });

        // 2. Unloading Trackings
        (activities.unloading || []).forEach((unloading) => {
            const details = unloading.unloadingDetails;
            const dateStr = details?.actualStartDate || details?.estimatedStartDate || unloading.createdAt || moment().toISOString();
            const itemDate = moment(dateStr);
            const rawStatus = (unloading.status || 'scheduled').toLowerCase();
            const isDone = rawStatus === 'completed' || rawStatus === 'done';
            const isInProg = rawStatus === 'in_progress' || rawStatus === 'running';
            const isOverdue = !isDone && itemDate.isBefore(today, 'day');
            const overdueDays = isOverdue ? today.diff(itemDate, 'days') : 0;

            const orderNum = unloading.order?.orderNumber || unloading.orderNumber;
            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (unloading.orderId ? `Order #${unloading.orderId.substring(0, 8)}` : '');

            items.push({
                id: `unload_${unloading.id}`,
                rawId: unloading.id,
                itemType: 'tracking',
                activityType: 'unloading',
                activityLabel: 'Bongkar (Unloading)',
                activityColor: '#8b5cf6',
                activityIcon: faDolly,
                orderId: unloading.orderId,
                orderNumber: orderLabel,
                customerName: (unloading.order as any)?.customer?.name,
                containers: unloading.container ? [{
                    id: unloading.container.id,
                    containerNumber: unloading.container.containerNumber || 'No Container',
                    sealNumber: unloading.container.sealNumber,
                    size: unloading.container.size
                }] : [],
                locationText: details?.address && details.address !== 'ededed' ? details.address : 'Pelabuhan / Gudang Bongkar',
                scheduledDate: dateStr,
                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),
                actualDate: details?.actualStartDate,
                isOverdue,
                overdueDays,
                status: rawStatus,
                normalizedStatus: isDone ? 'completed' : (isOverdue ? 'overdue' : (isInProg ? 'in_progress' : 'scheduled')),
                statusLabel: isDone ? 'Selesai' : (isInProg ? 'Sedang Bongkar' : 'Terjadwal'),
                detailsLink: `/orders/${unloading.orderId}/trackings`,
                orderLink: `/orders/${unloading.orderId}`,
                rawObject: unloading,
            });
        });

        // 3. Dooring Trackings
        (activities.dooring || []).forEach((dooring) => {
            const details = dooring.dooringDetails;
            const dateStr = details?.actualDepartureDate || details?.estimatedDepartureDate || dooring.createdAt || moment().toISOString();
            const itemDate = moment(dateStr);
            const rawStatus = (dooring.status || 'scheduled').toLowerCase();
            const isDone = rawStatus === 'completed' || rawStatus === 'arrived';
            const isInProg = rawStatus === 'in_progress' || rawStatus === 'in_transit' || rawStatus === 'departed';
            const isOverdue = !isDone && itemDate.isBefore(today, 'day');
            const overdueDays = isOverdue ? today.diff(itemDate, 'days') : 0;

            const orderNum = dooring.order?.orderNumber || dooring.orderNumber;
            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (dooring.orderId ? `Order #${dooring.orderId.substring(0, 8)}` : '');

            items.push({
                id: `door_${dooring.id}`,
                rawId: dooring.id,
                itemType: 'tracking',
                activityType: 'dooring',
                activityLabel: 'Dooring (Trucking)',
                activityColor: '#10b981',
                activityIcon: faTruck,
                truckRegNumber: details?.truck?.registrationNumber,
                vendorName: details?.truck?.vendor?.name,
                driverName: details?.driverName,
                driverPhone: details?.driverPhone,
                orderId: dooring.orderId,
                orderNumber: orderLabel,
                customerName: (dooring.order as any)?.customer?.name,
                containers: dooring.container ? [{
                    id: dooring.container.id,
                    containerNumber: dooring.container.containerNumber || 'No Container',
                    sealNumber: dooring.container.sealNumber,
                    size: dooring.container.size
                }] : [],
                originAddress: details?.originAddress,
                destinationAddress: details?.destinationAddress,
                locationText: details?.originAddress && details?.destinationAddress 
                    ? `${details.originAddress} ➔ ${details.destinationAddress}` 
                    : (details?.destinationAddress || 'Alamat Penerima'),
                scheduledDate: dateStr,
                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),
                estimatedArrival: details?.estimatedArrivalDate ? moment(details.estimatedArrivalDate).format('DD/MM/YYYY') : undefined,
                actualDate: details?.actualArrivalDate || details?.actualDepartureDate,
                isOverdue,
                overdueDays,
                status: rawStatus,
                normalizedStatus: isDone ? 'completed' : (isOverdue ? 'overdue' : (isInProg ? 'in_progress' : 'scheduled')),
                statusLabel: isDone ? 'Tiba di Tujuan' : (isInProg ? 'Dalam Perjalanan' : 'Terjadwal'),
                detailsLink: `/orders/${dooring.orderId}/trackings`,
                orderLink: `/orders/${dooring.orderId}`,
                rawObject: dooring,
            });
        });

        // 4. Ship Schedules
        (activities.shipSchedules || []).forEach((schedule) => {
            const departureDate = schedule.actualDeparture || schedule.estimatedDeparture;
            const arrivalDate = schedule.actualArrival || schedule.estimatedArrival;
            const dateStr = departureDate || schedule.createdAt || moment().toISOString();
            const itemDate = moment(dateStr);
            const rawStatus = (schedule.status || 'scheduled').toLowerCase();
            const isDone = rawStatus === 'completed' || rawStatus === 'arrived';
            const isInProg = rawStatus === 'sailing' || rawStatus === 'in_progress';
            const isOverdue = !isDone && itemDate.isBefore(today, 'day');
            const overdueDays = isOverdue ? today.diff(itemDate, 'days') : 0;

            const orders = (schedule.orders || []).filter(o => o && o.orderNumber);
            const firstOrder = orders.length > 0 ? orders[0] : undefined;
            const orderLabel = firstOrder ? `#${OrderService.getOrderNumber(String(firstOrder.orderNumber))}${orders.length > 1 ? ` (+${orders.length - 1})` : ''}` : '';

            items.push({
                id: `ship_${schedule.id}`,
                rawId: schedule.id,
                itemType: 'ship',
                activityType: 'ship_departure',
                activityLabel: rawStatus === 'sailing' ? 'Kapal Berlayar' : (rawStatus === 'arrived' ? 'Kapal Sandar' : 'Jadwal Kapal'),
                activityColor: '#0284c7',
                activityIcon: faShip,
                shipName: schedule.ship?.name || 'Kapal Pelayaran',
                voy: schedule.voy,
                vendorName: schedule.ship?.vendor?.name,
                orderId: firstOrder?.id,
                orderNumber: orderLabel || undefined,
                orders: orders.map(o => ({ id: o.id, orderNumber: String(o.orderNumber) })),
                originAddress: schedule.originAddress,
                destinationAddress: schedule.destinationAddress,
                locationText: schedule.originAddress && schedule.destinationAddress
                    ? `${schedule.originAddress} ➔ ${schedule.destinationAddress}`
                    : (schedule.originAddress || 'Pelabuhan Asal'),
                scheduledDate: dateStr,
                scheduledDateFormatted: `ETD: ${itemDate.format('DD/MM/YYYY')}`,
                estimatedArrival: arrivalDate ? moment(arrivalDate).format('DD/MM/YYYY') : undefined,
                actualDate: schedule.actualDeparture || schedule.actualArrival,
                isOverdue,
                overdueDays,
                status: rawStatus,
                normalizedStatus: isDone ? 'completed' : (isOverdue ? 'overdue' : (isInProg ? 'in_progress' : 'scheduled')),
                statusLabel: rawStatus === 'sailing' ? 'Sedang Berlayar' : (rawStatus === 'arrived' ? 'Sudah Sandar' : (isDone ? 'Selesai' : 'Terjadwal')),
                detailsLink: `/operationals/ships/${schedule.ship?.id}/schedules/${schedule.id}`,
                orderLink: firstOrder ? `/orders/${firstOrder.id}` : undefined,
                rawObject: schedule,
            });
        });

        // Sort items: overdue first, then chronological
        return items.sort((a, b) => {
            if (a.isOverdue && !b.isOverdue) return -1;
            if (!a.isOverdue && b.isOverdue) return 1;
            return moment(a.scheduledDate).valueOf() - moment(b.scheduledDate).valueOf();
        });
    }, [activities]);

    // KPI Summary Counts
    const counts = useMemo(() => {
        let total = allItems.length;
        let overdue = allItems.filter(i => i.isOverdue).length;
        let inProgress = allItems.filter(i => i.normalizedStatus === 'in_progress').length;
        let completed = allItems.filter(i => i.normalizedStatus === 'completed').length;
        return { total, overdue, inProgress, completed };
    }, [allItems]);

    // Filtered Items based on active filters & search
    const filteredItems = useMemo(() => {
        return allItems.filter((item) => {
            // Status Filter
            if (statusFilter === 'overdue' && !item.isOverdue) return false;
            if (statusFilter === 'scheduled' && item.normalizedStatus !== 'scheduled') return false;
            if (statusFilter === 'in_progress' && item.normalizedStatus !== 'in_progress') return false;
            if (statusFilter === 'completed' && item.normalizedStatus !== 'completed') return false;

            // Type Filter
            if (typeFilter === 'ship' && item.itemType !== 'ship') return false;
            if (typeFilter === 'loading' && item.activityType !== 'loading') return false;
            if (typeFilter === 'unloading' && item.activityType !== 'unloading') return false;
            if (typeFilter === 'dooring' && item.activityType !== 'dooring') return false;

            // Search Filter
            if (searchKeyword.trim() !== '') {
                const q = searchKeyword.toLowerCase();
                const matchShip = item.shipName?.toLowerCase().includes(q);
                const matchVoy = item.voy?.toLowerCase().includes(q);
                const matchOrder = item.orderNumber?.toLowerCase().includes(q) || item.orders?.some(o => o.orderNumber.toLowerCase().includes(q) || OrderService.getOrderNumber(o.orderNumber).toLowerCase().includes(q));
                const matchCust = item.customerName?.toLowerCase().includes(q);
                const matchLoc = item.locationText?.toLowerCase().includes(q);
                const matchTruck = item.truckRegNumber?.toLowerCase().includes(q);
                const matchDriver = item.driverName?.toLowerCase().includes(q);
                const matchContainer = item.containers?.some(c => c.containerNumber.toLowerCase().includes(q));

                if (!matchShip && !matchVoy && !matchOrder && !matchCust && !matchLoc && !matchTruck && !matchDriver && !matchContainer) {
                    return false;
                }
            }

            return true;
        });
    }, [allItems, statusFilter, typeFilter, searchKeyword]);

    const handleDateChange = (startDate: string, endDate: string) => {
        setSelectedDate({ startDate, endDate });
    };

    return (
        <div className={styles.container}>
            {/* Header, KPI Bar & Filters */}
            <WorkspaceHeader
                counts={counts}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                searchKeyword={searchKeyword}
                onSearchChange={setSearchKeyword}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRefresh={loadActivities}
            />

            {/* Main Content Area */}
            {!loaded ? (
                <LoadingComponent label="Memuat aktivitas operasional..." labelPosition="right" />
            ) : (
                <>
                    {viewMode === 'table' ? (
                        <WorkspaceTableView
                            items={filteredItems}
                            onQuickStatus={(item) => setActiveStatusItem(item)}
                            onReschedule={(item) => setActiveRescheduleItem(item)}
                        />
                    ) : (
                        <WorkspaceBoardView
                            items={filteredItems}
                            onQuickStatus={(item) => setActiveStatusItem(item)}
                            onReschedule={(item) => setActiveRescheduleItem(item)}
                        />
                    )}
                </>
            )}

            {/* Quick Status Modal */}
            {activeStatusItem && (
                <QuickStatusModal
                    item={activeStatusItem}
                    isOpen={true}
                    onDismiss={(refresh) => {
                        setActiveStatusItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}

            {/* Reschedule Modal */}
            {activeRescheduleItem && (
                <RescheduleModal
                    item={activeRescheduleItem}
                    isOpen={true}
                    onDismiss={(refresh) => {
                        setActiveRescheduleItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}
        </div>
    );
};

export default OperationsWorkspace;
